import * as Sentry from "@sentry/vue";
import { getAuth, signOut, type User as FirebaseUser } from "firebase/auth";
import type { Pinia, Store } from "pinia";
import { defineStore, getActivePinia } from "pinia";
import { computed, nextTick, shallowRef, watch } from "vue";
import { usePostHog } from "@/composables/usePostHog";
import { useQuery, useZero, setIdToken } from "@/composables/useZero";
import { queries } from "@loanlink-nl/zero";
import { useCookies } from "@vueuse/integrations/useCookies";
import router from "@/router";

export async function redirectAfterLogin() {
	const query = router.currentRoute.value.query;
	const next = query["next"];
	if (typeof next === "string" && next.startsWith("/")) {
		await router.push(next);
	} else {
		await router.push({ name: "App" });
	}
}

function getRootDomain(domain: string): string {
	const parts = domain.split(".");
	if (parts.length > 2) {
		return parts.slice(-2).join(".");
	}
	return domain;
}

interface ExtendedPinia extends Pinia {
	_s: Map<string, Store>;
}
function $ResetPinia(): void {
	const pinia = getActivePinia() as ExtendedPinia;
	if (!pinia) {
		throw new Error("There is no stores");
	}

	pinia._s.forEach((store) => {
		try {
			if ("$reset" in store) {
				store.$reset();
			}
			// eslint-disable-next-line no-empty
		} catch {}
	});
}

export const useUserStore = defineStore("user", () => {
	const { data: user } = useQuery(queries.user.me());
	const firebaseUser = shallowRef<FirebaseUser | null>(null);
	const role = computed(() => user.value?.role);
	let colors: Record<string, { bg: string; text: string }> = {};

	function $reset() {
		firebaseUser.value = null;
		colors = {};
	}

	async function setUser(newUser: FirebaseUser | null) {
		if (firebaseUser.value?.uid !== newUser?.uid) {
			const hasToken = !!newUser;

			$ResetPinia();
			firebaseUser.value = newUser;

			const cookies = useCookies(["isLoggedIn"]);
			cookies.set("isLoggedIn", hasToken, {
				domain: getRootDomain(window.location.hostname),
				path: "/",
			});

			if (!hasToken) {
				return;
			}
		}

		if (!newUser) {
			return;
		}

		const { token, claims } = await newUser.getIdTokenResult();
		setIdToken(token, claims);

		const zero = useZero();
		await nextTick();

		const currentUser = await zero.value.run(queries.user.me());
		if (currentUser) {
			return currentUser;
		}

		return await zero.value.run(queries.user.me(), { type: "complete" });
	}

	watch(user, (newUser) => {
		const { posthog } = usePostHog();

		if (!newUser) {
			Sentry.setUser(null);
			posthog.reset();
			return;
		}

		posthog.identify(newUser.id, {
			email: newUser.emailAddress,
			name: newUser.fullName,
			agencyId: newUser.agencyId,
		});

		Sentry.setUser({
			fullName: newUser.fullName,
			email: newUser.emailAddress ?? undefined,
		});
	});

	async function logout() {
		const auth = getAuth();
		await signOut(auth);
		$ResetPinia();
	}

	return {
		user,
		role,
		setUser,
		logout,
		colors,

		$reset,
	};
});
